<template>
  <div class="global-recorder-control" data-recorder-control="true">
    <!-- 录制控制按钮 - 合并为一个按钮 -->
    <el-button
      type="text"
      @click="toggleRecording"
      :class="['record-btn', isRecording ? 'stop-btn' : 'start-btn']"
      data-recorder-control="true"
    >
      {{ isRecording ? "停止录制" : "开始录制" }}
    </el-button>

    <!-- 生成自然语言按钮 -->
    <!-- <button 
      @click="generateNaturalLanguage" 
      :disabled="!recordingData"
      class="record-btn script-btn"
    >
      生成自然语言步骤
    </button> -->

    <!-- 状态提示 -->
    <!-- <p class="status" v-if="status">{{ status }}</p> -->

    <!-- 自然语言结果展示 -->
    <!-- <div v-if="naturalLanguageScript" class="script-container">
      <h4>自然语言执行步骤：</h4>
      <pre>{{ naturalLanguageScript }}</pre>
    </div> -->
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

// 状态管理
const isRecording = ref(false);
const status = ref("");
const recordingData = ref(null); // 存储录制原始数据
const naturalLanguageScript = ref(""); // 自然语言脚本
let recorder = null;
let midscene = null;

// 挂载时加载Midscene脚本
onMounted(async () => {
  try {
    const script = document.createElement("script");
    script.src = "/js/midscene-web.umd.js";
    script.async = true;

    await new Promise((resolve, reject) => {
      script.onload = () => {
        midscene = window.midscene;
        if (midscene) resolve();
        else reject(new Error("Midscene 脚本加载失败"));
      };
      script.onerror = () => reject(new Error("Midscene 脚本加载失败"));
      document.body.appendChild(script);
    });

    status.value = "脚本加载完成，可开始录制";
  } catch (err) {
    status.value = "初始化失败：" + err.message;
    console.error("初始化错误：", err);
  }
});

// 切换录制状态
const toggleRecording = async () => {
  if (isRecording.value) {
    // 当前正在录制，停止录制
    await stopRecording();
  } else {
    // 当前未录制，开始录制
    await startRecording();
  }
};

// 开始录制
const startRecording = async () => {
  if (!midscene) {
    status.value = "请先等待脚本加载完成";
    return;
  }
  try {
    recorder = new midscene.Recorder(window, {
      // 配置录制选项，包含更多上下文信息以优化自然语言生成
      captureMetadata: true,
      captureElementText: true,
      captureInputValues: true,
    });
    // 监听路由变化，记录页面切换
    if (window.$router) {
      window.$router.afterEach((to) => {
        if (isRecording.value) {
          recorder.actions.push({
            type: "page_switch",
            fromUrl: window.location.href,
            toUrl: to.fullPath,
            timestamp: Date.now(),
          });
        }
      });
    }
    await recorder.start();
    isRecording.value = true;
    status.value = "录制中... 可执行点击、输入等操作";
    naturalLanguageScript.value = ""; // 清空之前的脚本
  } catch (err) {
    status.value = "启动录制失败：" + err.message;
    console.error("录制启动错误：", err);
  }
};

// 停止录制并保存数据
const stopRecording = async () => {
  if (!recorder) return;
  try {
    const data = await recorder.stop();
    recordingData.value = data; // 保存原始录制数据

    // 生成带自然语言的文件并下载
    const enhancedData = {
      ...data,
      initialUrl: data.url, // 开始录制时的URL
      finalUrl: data.finalUrl, // 停止录制时的URL
      createdAt: new Date().toISOString(),
      naturalLanguage: await generateNaturalLanguageScript(data), // 提前生成自然语言
    };

    const blob = new Blob([JSON.stringify(enhancedData, null, 2)], {
      type: "application/json; charset=utf-8",
    });
    const downloadLink = document.createElement("a");
    downloadLink.href = URL.createObjectURL(blob);
    downloadLink.download = `midscene-recording-${Date.now()}.json`;
    downloadLink.click();

    isRecording.value = false;
    status.value = "录制完成！文件已下载（包含自然语言步骤）";
    recorder = null;
  } catch (err) {
    status.value = "停止录制失败：" + err.message;
    console.error("录制停止错误：", err);
  }
};

// 生成自然语言脚本（核心功能）
const generateNaturalLanguageScript = async (data) => {
  if (!midscene || !data) return "";
  try {
    // 使用Midscene的generateScript方法生成自然语言描述
    const script = await midscene.generateScript(data, {
      format: "natural-language", // 指定输出为自然语言
      language: "zh-CN", // 中文输出
      detailLevel: "medium", // 中等详细程度
    });
    return script;
  } catch (err) {
    console.error("生成自然语言失败：", err);
    return `生成失败：${err.message}`;
  }
};

// 手动生成自然语言（针对已加载的录制数据）
const generateNaturalLanguage = async () => {
  if (!recordingData.value) {
    status.value = "请先完成录制或加载录制文件";
    return;
  }
  status.value = "正在生成自然语言步骤...";
  naturalLanguageScript.value = await generateNaturalLanguageScript(
    recordingData.value
  );
  status.value = "自然语言步骤生成完成";
};

// 组件卸载清理
onUnmounted(() => {
  if (recorder) {
    recorder.stop().catch((err) => console.error("卸载时停止录制错误：", err));
  }
  const script = document.querySelector(
    'script[src="/js/midscene-web.umd.js"]'
  );
  if (script) document.body.removeChild(script);
  midscene = null;
});
</script>

<style scoped>
.global-recorder-control {
  z-index: 9999;
  background: white;
  padding: 15px;
  border-radius: 150px;
  width: 300px;
}
.recorder-status {
  margin-top: 10px;
  padding: 10px;
  background: #f9f9f9;
  border-radius: 4px;
  font-size: 12px;
  color: #666;
  line-height: 1.5;
}
.record-btn {
  border: none;
  cursor: pointer;
  font-size: 14px;
}
.record-btn.start {
  background: #52c41a;
  color: white;
}
.record-btn.stop {
  background: #ff4d4f;
  color: white;
}
.record-btn.export {
  background: #1890ff;
  color: white;
}
.record-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
}
.step-list {
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
}
.step-item {
  padding: 5px 0;
  border-bottom: 1px solid #eee;
  font-size: 12px;
}
.step-item:last-child {
  border-bottom: none;
}

.status {
  font-size: 12px;
  color: #ccc;
}
</style>